import { faCheckCircle, faFilePdf, faSpinner, faTimes, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Tab, Tabs } from "react-bootstrap";
import InputCPF from "./InputCPF";
import Globals from "../Globals";
import api from '../services/api';
import Swal from "sweetalert2";

export default function BudgetInfoModal({
  show,
  onHide,
  selectedBudget,
  onSave
}) {
  const [activeTab, setActiveTab] = useState("pdf");

  const [fullName, setFullName] = useState("");
  const [fullNameDisabled, setFullNameDisabled] = useState(true);
  const [customerCpf, setCustomerCpf] = useState("");
  const [customerBirth, setCustomerBirth] = useState("");
  const [customerFound, setCustomerFound] = useState(false);
  const [professionalName, setProfessionalName] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [loadingInitialDoctor, setLoadingInitialDoctor] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [doctorSuggestions, setDoctorSuggestions] = useState([]);
  const [showDoctorSuggestions, setShowDoctorSuggestions] = useState(false);
  const [isSearchingDoctor, setIsSearchingDoctor] = useState(false);
  const [prescriptionType, setPrescriptionType] = useState("normal");
  const [loading, setLoading] = useState(false);

  // Limpa o formulário quando o modal é aberto
  useEffect(() => {
    if (show && selectedBudget) {
      // Clear form data
      setFullName("");
      setCustomerCpf("");
      setCustomerBirth("");
      setCustomerFound(false);
      setProfessionalName("");
      setDoctorSuggestions([]);
      setSelectedDoctor(null);
      setShowDoctorSuggestions(false);
      setSelectedCustomer(null);
      setPrescriptionType("normal");

      // Busca inicial do médico se já tem o nome
      if (selectedBudget.professional_name) {
        setLoadingInitialDoctor(true);

        api.get(`api/professionals/search/${selectedBudget.professional_name}`)
          .then(response => {
            if (response.data && response.data.data && response.data.data.length > 0) {
              setSelectedDoctor(response.data.data[0]);
            } else {
              setProfessionalName("");
            }
          })
          .catch(error => {
            console.error('Erro ao buscar médico:', error);
          })
          .finally(() => {
            setLoadingInitialDoctor(false);
          });
      }
    }
  }, [show, selectedBudget]);

  // Busca de cliente por CPF e data de nascimento
  useEffect(() => {
    const searchCustomer = async (cpf, birth) => {
      const onlyDigits = (str) => str.replace(/\D/g, '');
      const customerCpf = onlyDigits(cpf);

      const year = birth.split('-')[0];
      const yearHasLeadingZero = year.length === 4 && year.startsWith('0');

      if (customerCpf.length < 11 || birth.length === 0 || yearHasLeadingZero) return;

      setLoading(true);

      await api.post('api/customers/search/cpf/birth', {
        cpf: customerCpf,
        birth
      })
        .then(response => {
          if (response.data.data.length !== 0) {
            setCustomerFound(true);
            setSelectedCustomer(response.data.data);
            setFullName(response.data.data.name);
          } else {
            setCustomerFound(false);
            setFullName('');
          }
          setFullNameDisabled(false);
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    const timer = setTimeout(() => {
      searchCustomer(customerCpf, customerBirth);
    }, 500);

    return () => clearTimeout(timer);
  }, [customerCpf, customerBirth]);

  // Busca de médicos
  useEffect(() => {
    const searchDoctors = async (name) => {
      if (name.length < 3) {
        setDoctorSuggestions([]);
        setShowDoctorSuggestions(false);
        return;
      }

      setIsSearchingDoctor(true);
      setShowDoctorSuggestions(true);

      try {
        const response = await api.get(`api/professionals/search/${encodeURIComponent(name)}`);

        if (response.data && response.data.data) {
          setDoctorSuggestions(response.data.data);
          setShowDoctorSuggestions(true);
        } else {
          setDoctorSuggestions([]);
          setShowDoctorSuggestions(false);
        }
      } catch (error) {
        console.error('Erro ao buscar médicos:', error);
        setDoctorSuggestions([]);
        setShowDoctorSuggestions(false);
      } finally {
        setIsSearchingDoctor(false);
      }
    };

    const timer = setTimeout(() => {
      searchDoctors(professionalName);
    }, 300);

    return () => clearTimeout(timer);
  }, [professionalName]);

  const selectDoctor = (doctor) => {
    setProfessionalName(doctor.name);
    setSelectedDoctor(doctor);
    setShowDoctorSuggestions(false);
    setDoctorSuggestions([]);
  };

  const removeSelectedDoctor = () => {
    setSelectedDoctor(null);
    setDoctorSuggestions([]);
    setProfessionalName("");
  };

  const handleDoctorInputBlur = () => {
    // Timeout para permitir o clique na sugestão antes de esconder
    setTimeout(() => {
      setShowDoctorSuggestions(false);
    }, 200);
  };

  const saveAttendanceInfos = async (e) => {
    e.preventDefault();

    if (!customerCpf) {
      return Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'CPF do paciente é obrigatório.',
        toast: true,
        position: 'top',
      });
    }

    onHide();

    Swal.fire({
      icon: 'info',
      title: 'Aguarde...',
      text: 'Salvando dados',
      allowOutsideClick: false,
      showConfirmButton: false,
      showCancelButton: false,
      showCloseButton: false
    });

    let currentCustomer = selectedCustomer;

    if (!customerFound) {
      await api.post('api/customers', {
        cpf: customerCpf.replaceAll(".", "").replaceAll("-", ""),
        name: fullName,
        birth: customerBirth,
        mobile: selectedBudget.mobile.replaceAll(' ', '').replaceAll('(', '').replaceAll(')', '').replaceAll('-', ''),
        dependent: false
      })
        .then((response => {
          currentCustomer = response.data.data;
          setSelectedCustomer(currentCustomer);
        }));
    }

    await api.put(`api/client/budgets/${selectedBudget.attendance_id}`, {
      status: 'closed'
    })
      .then(async (response) => {
        await api.post('api/attendances/create/pharmacy', {
          cpf: customerCpf.replaceAll(".", "").replaceAll("-", ""),
          name: fullName,
          birth: customerBirth,
          customer_id: currentCustomer.id,
          customerId_foreign: currentCustomer.id,
          shopper_id: 14, // fixo achemed
          professional_id: selectedDoctor.id,
          professionalId_foreign: selectedDoctor.id,
          professional_cpf: selectedDoctor.cpf,
          date_scheduling: selectedBudget.date_scheduling.split(' ')[0],
          scheduling_id: 0,
          type: 0,
          procedure_name: 'CONSULTA EM HORÁRIO NORMAL OU PREESTABELECIDO',
          procedure_id: 324,
          access_url: Globals.BASE_URL,
          status: 0,
          agreement_name: 'ACHEMED',
          file_path: selectedBudget.image_path,
          doc_type: prescriptionType === "normal" ? '1' : '8',
          text: '<h1>Importado da impressora</h1>'
        });

        Swal.close();

        Swal.fire({
          icon: 'success',
          title: 'Sucesso',
          text: 'Informações do atendimento salvas com sucesso.'
        });

        if (onSave) onSave();
      })
      .catch(error => {
        console.error('Error updating attendance:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Ocorreu um erro ao salvar as informações do atendimento.'
        });
      });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Informações do atendimento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="gy-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Col lg={6}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
               {
                selectedBudget?.image_path_pre && (
                  <Tab eventKey="pre" title="Pré-orçamento">
                    <div className="border rounded bg-light p-3 h-100 d-flex justify-content-center align-items-center">
                      {selectedBudget?.image_path_pre ? (
                        <div className="w-100 h-100" style={{ minHeight: '500px' }}>
                          <iframe
                            src={`${Globals.BASE_URL}/storage/${selectedBudget.image_path_pre}#navpanes=0`}
                            width="100%"
                            height="100%"
                            style={{ border: 'none', minHeight: '500px' }}
                            title="Visualização do arquivo de pré-orçamento"
                          />
                        </div>
                      ) : (
                        <div className="text-center text-muted">
                          <FontAwesomeIcon icon={faFilePdf} size="4x" className="mb-3" />
                          <div className="h5 fw-semibold">PDF não disponível</div>
                          <div className="text-muted small">
                            Nenhum arquivo PDF foi encontrado para este atendimento.
                          </div>
                        </div>
                      )}
                    </div>
                  </Tab>
                ) 
              }
              
              <Tab eventKey="pdf" title="Arquivo da solicitação">
                <div className="border rounded bg-light p-3 h-100 d-flex justify-content-center align-items-center">
                  {selectedBudget?.image_path ? (
                    <div className="w-100 h-100" style={{ minHeight: '500px' }}>
                      <iframe
                        src={`${Globals.BASE_URL}/storage/${selectedBudget.image_path}#navpanes=0`}
                        width="100%"
                        height="100%"
                        style={{ border: 'none', minHeight: '500px' }}
                        title="Visualização do arquivo da prescrição"
                      />
                    </div>
                  ) : (
                    <div className="text-center text-muted">
                      <FontAwesomeIcon icon={faFilePdf} size="4x" className="mb-3" />
                      <div className="h5 fw-semibold">PDF não disponível</div>
                      <div className="text-muted small">
                        Nenhum arquivo PDF foi encontrado para este atendimento.
                      </div>
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </Col>

          <Col lg={6}>
            <h5 className="fw-semibold">Informações do paciente</h5>
            <Form onSubmit={saveAttendanceInfos}>
              <div className="mb-3 d-flex">
                <Form.Group className="me-5 col-5">
                  <Form.Label>CPF</Form.Label>
                  <InputCPF
                    value={customerCpf}
                    onChange={setCustomerCpf}
                  />
                </Form.Group>

                <Form.Group className="col-6">
                  <Form.Label>Data de nascimento</Form.Label>
                  <Form.Control
                    type="date"
                    value={customerBirth}
                    onChange={(e) => setCustomerBirth(e.target.value)}
                    required
                  />
                </Form.Group>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Nome completo</Form.Label>
                <Form.Control
                  value={loading ? 'BUSCANDO PACIENTE. AGUARDE ...' : fullName}
                  onChange={(e) => setFullName(e.target.value.toUpperCase())}
                  placeholder="Nome completo do paciente"
                  required
                  disabled={fullNameDisabled || customerFound}
                  className="customer-name-input"
                />
              </Form.Group>

              {customerFound && (
                <span className="text-gray">
                  <FontAwesomeIcon icon={faCheckCircle} color="green" className="me-3" />
                  Paciente já cadastrado
                </span>
              )}

              <hr className="my-4" />

              <h5 className="fw-semibold">Informações do profissional</h5>

              <Form.Group className="mb-4">
                <Form.Label>Nome</Form.Label>
                <Form.Control
                  value={professionalName}
                  onChange={(e) => setProfessionalName(e.target.value)}
                  onFocus={() => setShowDoctorSuggestions(doctorSuggestions.length > 0)}
                  onBlur={handleDoctorInputBlur}
                  placeholder="Buscar nome do profissional"
                  disabled={selectedDoctor !== null}
                  required
                />
                {(showDoctorSuggestions && !selectedDoctor) && (
                  <div
                    className="position-absolute bg-white border rounded shadow-sm mt-1"
                    style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto', width: '40%' }}
                  >
                    {isSearchingDoctor && (
                      <div className="p-2 text-muted text-center">
                        <small>Buscando médicos...</small>
                      </div>
                    )}

                    {!isSearchingDoctor && doctorSuggestions.length === 0 && (
                      <div className="p-2 text-muted text-center">
                        <small>Nenhum médico encontrado</small>
                      </div>
                    )}

                    {!isSearchingDoctor && doctorSuggestions.map((doctor, index) => (
                      <div
                        key={index}
                        className="p-2 border-bottom cursor-pointer hover-bg-light"
                        style={{ cursor: 'pointer' }}
                        onMouseDown={() => selectDoctor(doctor)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <div className="fw-semibold">{doctor.name}</div>
                        <small className="text-muted">
                          {doctor.council_name} - {doctor.council_state}: {doctor.council_number}
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </Form.Group>

              {loadingInitialDoctor && (
                <div className="mb-4">
                  <Card className="border-primary">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white me-3"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FontAwesomeIcon icon={faSpinner} spin />
                        </div>
                        <div>
                          <div className="fw-semibold text-primary mb-1">
                            Buscando Profissional...
                          </div>
                          <div className="text-muted small">
                            Aguarde enquanto verificamos os dados do profissional
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              )}

              {(selectedDoctor && !loadingInitialDoctor) && (
                <div className="mb-4">
                  <Card className="border-success">
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle bg-success d-flex align-items-center justify-content-center text-white me-3"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <FontAwesomeIcon icon={faUser} />
                          </div>
                          <div>
                            <div className="fw-semibold text-success mb-1">
                              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                              Profissional Selecionado
                            </div>
                            <div className="fw-bold">{selectedDoctor.name}</div>
                            <div className="text-muted small">
                              {selectedDoctor.council_name} - {selectedDoctor.council_state}: {selectedDoctor.council_number}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={removeSelectedDoctor}
                          title="Remover profissional selecionado"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              )}

              <hr className="my-4" />

              <h5 className="fw-semibold">Tipo de receituário</h5>

              <Form.Group className="d-flex gap-4 mt-3 mb-5">
                <Form.Check
                  type="radio"
                  label="Receituário Normal"
                  id="normal"
                  name="receituario"
                  value="normal"
                  checked={prescriptionType === "normal"}
                  onChange={(e) => setPrescriptionType(e.target.value)}
                />

                <Form.Check
                  type="radio"
                  label="Receituário Especial"
                  id="especial"
                  name="receituario"
                  value="especial"
                  checked={prescriptionType === "especial"}
                  onChange={(e) => setPrescriptionType(e.target.value)}
                />
              </Form.Group>

              <div className="d-flex gap-2 pt-3">
                <Button variant="outline-secondary" className="flex-grow-1" onClick={onHide}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-grow-1"
                  variant="success"
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Salvar Dados
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}